import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { ModelError_Item } from '@treadinc/horizon-api-spec';
import { isAxiosError } from 'axios';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
  LoadingSpinner,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { RoutingSettings } from '~hooks/useRoutingSettings/models';
import useRoutingSettings from '~hooks/useRoutingSettings/useRoutingSettings';
import SettingsPageHeader from '~pages/Settings/components/SettingsPageHeader';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import RouteOptimizationForm from './RouteOptimizationForm';
import {
  RoutingSettingsDto,
  routingSettingsSchema,
  setRoutingSettingsSchemaDefaultValues,
} from './routingSettingsSchema';

const SERVER_FIELD_TO_FORM_FIELD: Record<string, keyof RoutingSettingsDto> = {
  interstate_drive_minutes: 'interstateDriveHours',
  interstate_total_minutes: 'interstateTotalHours',
  intrastate_drive_minutes: 'intrastateDriveHours',
  intrastate_total_minutes: 'intrastateTotalHours',
  min_loads_per_truck: 'minLoadsPerTruck',
  max_loads_per_truck: 'maxLoadsPerTruck',
  max_orders_per_truck: 'maxOrdersPerTruck',
};

const RouteOptimizationManagement = observer(() => {
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { fetch, update } = useRoutingSettings();

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasLoadedSettings, setHasLoadedSettings] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isNotConfigured, setIsNotConfigured] = useState(false);
  const [isLoadFailed, setIsLoadFailed] = useState(false);
  const loadGenerationRef = useRef(0);

  const {
    control,
    formState: { isDirty },
    handleSubmit,
    reset,
    setError,
  } = useForm<RoutingSettingsDto>({
    resolver: yupResolver(routingSettingsSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: setRoutingSettingsSchemaDefaultValues(null),
  });

  const loadSettings = useCallback(async () => {
    if (!companyId) {
      return;
    }

    const generation = loadGenerationRef.current + 1;
    loadGenerationRef.current = generation;

    // The schema defaults that stand in for an absent row are generic hours-of-service
    // limits, not this company's, so Save stays closed until real values arrive.
    setHasLoadedSettings(false);
    setIsEditing(false);
    setIsLoadFailed(false);
    setIsLoading(true);

    const routingSettings = await fetch(companyId, null);

    // A slower fetch for a company the user has already left must not land on the form.
    if (loadGenerationRef.current !== generation) {
      return;
    }

    // A null resolves to no stored row, which the backfill should have prevented. It
    // gets its own empty state so it is diagnosable, rather than reading as a blip.
    setIsLoading(false);

    // The hook has already toasted the error; showing the form here would present the
    // generic defaults as this company's values.
    if (routingSettings === undefined) {
      setIsLoadFailed(true);
      return;
    }

    setIsNotConfigured(routingSettings === null);
    setHasLoadedSettings(Boolean(routingSettings));
    reset(setRoutingSettingsSchemaDefaultValues(null, routingSettings ?? undefined));
  }, [companyId, fetch, reset]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleSave = handleSubmit(async (data) => {
    if (!companyId) {
      return;
    }

    const generation = loadGenerationRef.current;
    setIsSaving(true);

    try {
      const saved = await update(companyId, RoutingSettings.deparseUpdate(data));

      // Switching company reloads the form under an in-flight save. Without this the
      // old company's response resets the newly loaded form, and the next Save writes
      // those values to the new company.
      if (loadGenerationRef.current !== generation) {
        return;
      }

      reset(setRoutingSettingsSchemaDefaultValues(null, saved));
      setIsEditing(false);
      toasterStore.push(alert(t('success_messages.changes_saved'), AlertTypes.success));
    } catch (error) {
      // The hook re-throws a 422 untoasted so the form can own it; everything else
      // it has already surfaced.
      const isStaleSave = loadGenerationRef.current !== generation;
      const isFieldValidationError =
        isAxiosError(error) && error.response?.status === 422;

      if (!isStaleSave && isFieldValidationError) {
        const serverErrors = (error.response?.data?.error?.errors ??
          []) as ModelError_Item[];
        let anyFieldMatched = false;

        serverErrors.forEach(({ field, message }) => {
          const formField = SERVER_FIELD_TO_FORM_FIELD[field];

          if (formField) {
            anyFieldMatched = true;
            setError(formField, { type: 'manual', message });
          }
        });

        // A rejection the field map does not cover has no field to render on, so it
        // would otherwise be invisible.
        if (!anyFieldMatched) {
          toasterStore.push(
            alert(t('form_validation_errors.form_has_errors'), AlertTypes.error),
          );
        }
      }
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <>
      <SettingsPageHeader
        title={t('administration.route_optimization.title')}
        description={t('settings.page_descriptions.route_optimization')}
      />

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <LoadingSpinner isVisible loadingIndicatorSize={24} />
        </Box>
      ) : isNotConfigured ? (
        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontWeight: 600 }}>
            {t('administration.route_optimization.not_configured')}
          </Typography>
          <Typography sx={{ color: 'text.secondary', fontSize: 14, mt: 1 }}>
            {t('administration.route_optimization.not_configured_help')}
          </Typography>
        </Box>
      ) : isLoadFailed ? (
        <Box sx={{ mt: 4 }}>
          <Typography sx={{ fontWeight: 600 }}>
            {t('error_messages.routing_settings.failed_to_fetch')}
          </Typography>
          <SmallButton
            color="secondary"
            onClick={loadSettings}
            sx={{ mt: 2 }}
            variant="outlined"
          >
            {t('actions.retry')}
          </SmallButton>
        </Box>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', mt: 2 }}>
            {isEditing ? (
              <SmallButton
                color="secondary"
                disabled={isSaving}
                onClick={loadSettings}
                variant="outlined"
              >
                {t('actions.cancel')}
              </SmallButton>
            ) : (
              <SmallButton
                color="brandV2Yellow"
                disabled={!hasLoadedSettings}
                onClick={() => setIsEditing(true)}
              >
                {t('actions.edit')}
              </SmallButton>
            )}

            <SmallLoadingButton
              color="brandV2Yellow"
              disabled={!isEditing || !isDirty || !hasLoadedSettings}
              loading={isSaving}
              onClick={handleSave}
            >
              {t('actions.update')}
            </SmallLoadingButton>
          </Box>

          <RouteOptimizationForm
            control={control}
            disabled={isSaving}
            editable={isEditing}
          />
        </>
      )}
    </>
  );
});

export default RouteOptimizationManagement;
